import { useMemo, useState } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FileText,
  Sparkles,
  FolderTree,
  Plus,
  Pencil,
  Trash2,
  Copy,
} from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import clsx from 'clsx';
import { api } from '../lib/api';
import { ModuleSwitcher } from './ModuleSwitcher';
import { ThemeToggle } from './ThemeToggle';

type TreeNode = {
  id: string;
  parentId: string | null;
  kind: 'folder' | 'project' | 'space' | 'document';
  title: string | null;
  icon: string | null;
  color: string | null;
  sortOrder: number;
};

interface SidebarProps {
  activeNodeId: string | null;
  onActivate: (id: string | null) => void;
}

export function Sidebar({ activeNodeId, onActivate }: SidebarProps) {
  const nodesQuery = api.treeNodes.list.useQuery();
  const utils = api.useUtils();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const moveMutation = api.treeNodes.move.useMutation({
    onSuccess: () => utils.treeNodes.list.invalidate(),
  });

  const tree = useMemo(() => {
    if (!nodesQuery.data) return null;
    const byParent = new Map<string | null, TreeNode[]>();
    for (const node of nodesQuery.data as TreeNode[]) {
      const parent = node.parentId ?? null;
      if (!byParent.has(parent)) byParent.set(parent, []);
      byParent.get(parent)!.push(node);
    }
    for (const list of byParent.values()) {
      list.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return byParent;
  }, [nodesQuery.data]);

  // Множество id узлов, которые должны быть видимы при текущем фильтре:
  // совпадение по title + все предки совпавших (чтобы видеть путь до матча).
  // Если поиск пустой — null означает «не фильтровать вообще».
  const matchedIds = useMemo<Set<string> | null>(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || !nodesQuery.data) return null;
    const all = nodesQuery.data as TreeNode[];
    const byId = new Map(all.map((n) => [n.id, n]));
    const visible = new Set<string>();
    for (const n of all) {
      if ((n.title ?? '').toLowerCase().includes(q)) {
        visible.add(n.id);
        // Добавляем всех предков, чтобы родительские папки/проекты остались видимы
        let p = n.parentId;
        while (p) {
          if (visible.has(p)) break;
          visible.add(p);
          p = byId.get(p)?.parentId ?? null;
        }
      }
    }
    return visible;
  }, [searchQuery, nodesQuery.data]);

  // При активном поиске автоматически раскрываем все предки матчей,
  // иначе пользователь не увидит вложенные совпадения.
  const effectiveExpanded = useMemo(() => {
    if (!matchedIds) return expanded;
    return matchedIds; // в режиме поиска все matched (и предки) открыты
  }, [matchedIds, expanded]);

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const dragId = e.active.id as string;
    const overId = e.over?.id as string | undefined;
    if (!overId || dragId === overId) return;

    type DropData =
      | { type: 'into'; nodeId: string | null }
      | { type: 'before'; nodeId: string }
      | { type: 'after'; nodeId: string };
    const dropData = e.over?.data.current as DropData | undefined;
    if (!dropData) return;
    if (!nodesQuery.data) return;

    const allById = new Map((nodesQuery.data as TreeNode[]).map((n) => [n.id, n]));

    if (dropData.type === 'into') {
      const targetParentId = dropData.nodeId;
      // Не вкладывать узел сам в себя или в свой потомок
      if (dragId === targetParentId) return;
      if (targetParentId && isDescendantOf(targetParentId, dragId, allById)) return;
      const siblings = (tree?.get(targetParentId) ?? []).filter((n) => n.id !== dragId);
      const newSort = siblings.reduce((max, s) => Math.max(max, s.sortOrder), 0) + 1;
      moveMutation.mutate({
        id: dragId,
        newParentId: targetParentId,
        newSortOrder: newSort,
      });
      return;
    }

    // before/after — reorder в том же родителе target-а
    const target = allById.get(dropData.nodeId);
    if (!target) return;
    if (dragId === target.id) return;
    // Не пытаться поместить узел внутрь своего поддерева
    if (isDescendantOf(target.parentId, dragId, allById)) return;

    const targetParent = target.parentId;
    const siblings = (tree?.get(targetParent) ?? []).filter((n) => n.id !== dragId);
    const targetIdx = siblings.findIndex((s) => s.id === target.id);
    if (targetIdx === -1) return;

    let newSort: number;
    if (dropData.type === 'before') {
      const prev = siblings[targetIdx - 1];
      newSort = prev ? (prev.sortOrder + target.sortOrder) / 2 : target.sortOrder - 1;
    } else {
      const next = siblings[targetIdx + 1];
      newSort = next ? (target.sortOrder + next.sortOrder) / 2 : target.sortOrder + 1;
    }

    moveMutation.mutate({
      id: dragId,
      newParentId: targetParent,
      newSortOrder: newSort,
    });
  };

  return (
    <aside className="w-[260px] flex-shrink-0 border-r border-line-light dark:border-line-dark bg-surface2-light dark:bg-surface2-dark flex flex-col">
      <div className="px-3 py-2 border-b border-line-light dark:border-line-dark flex items-center gap-2">
        <ModuleSwitcher />
        <ThemeToggle />
      </div>
      <header className="px-4 py-3 border-b border-line-light dark:border-line-dark flex items-center justify-between">
        <h1 className="text-base font-semibold text-accent">Дерево</h1>
        <CreateMenu parentId={null} />
      </header>

      <SearchBox value={searchQuery} onChange={setSearchQuery} />


      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <nav className="flex-1 overflow-y-auto py-2">
          {nodesQuery.isLoading && (
            <div className="px-4 py-2 text-sm text-ink-faint_l dark:text-ink-faint_d">Загрузка…</div>
          )}
          {nodesQuery.isError && (
            <div className="px-4 py-2 text-sm text-red-500">
              Ошибка: {nodesQuery.error.message}
            </div>
          )}
          {nodesQuery.data && tree && (
            <>
              <RootDropZone />
              <TreeBranch
                parentId={null}
                tree={tree}
                depth={0}
                activeNodeId={activeNodeId}
                onActivate={onActivate}
                expanded={effectiveExpanded}
                toggleExpanded={toggleExpanded}
                matchedIds={matchedIds}
              />
              {matchedIds && matchedIds.size === 0 && (
                <div className="px-4 py-4 text-xs text-ink-faint_l dark:text-ink-faint_d text-center">
                  Ничего не найдено по запросу «{searchQuery}»
                </div>
              )}
            </>
          )}
          {nodesQuery.data && nodesQuery.data.length === 0 && (
            <div className="px-4 py-6 text-sm text-ink-faint_l dark:text-ink-faint_d text-center">
              <p className="mb-2">Дерево пустое</p>
              <p className="text-xs">Нажми «+» в шапке, чтобы создать первый узел</p>
            </div>
          )}
        </nav>
      </DndContext>
    </aside>
  );
}

function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="px-3 py-2 border-b border-line-light dark:border-line-dark relative">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Поиск по дереву…"
        className="w-full px-2.5 py-1 pr-6 text-xs rounded bg-surface3-light dark:bg-surface3-dark border border-transparent focus:border-accent outline-none placeholder:text-ink-faint_l dark:placeholder:text-ink-faint_d"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Очистить поиск"
          className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-faint_l dark:text-ink-faint_d hover:text-ink-light dark:hover:text-ink-dark text-xs leading-none"
        >
          ×
        </button>
      )}
    </div>
  );
}

function RootDropZone() {
  const { setNodeRef, isOver } = useDroppable({
    id: 'root-drop',
    data: { type: 'into', nodeId: null },
  });
  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'h-2 mx-2 rounded transition-colors',
        isOver && 'bg-accent/30',
      )}
    />
  );
}

interface TreeBranchProps {
  parentId: string | null;
  tree: Map<string | null, TreeNode[]>;
  depth: number;
  activeNodeId: string | null;
  onActivate: (id: string) => void;
  expanded: Set<string>;
  toggleExpanded: (id: string) => void;
  // null = поиск выключен, иначе только узлы из этого Set рендерятся.
  matchedIds: Set<string> | null;
}

function TreeBranch({
  parentId,
  tree,
  depth,
  activeNodeId,
  onActivate,
  expanded,
  toggleExpanded,
  matchedIds,
}: TreeBranchProps) {
  const children = tree.get(parentId) ?? [];
  const filtered = matchedIds ? children.filter((n) => matchedIds.has(n.id)) : children;
  // Возвращаем пустой <ul> вместо null когда фильтр оставил 0 совпадений —
  // иначе drop-into-folder ломается в search-mode (родитель остаётся видим
  // через matchedIds-предков, но без вложенного <ul> drop-target теряется).
  if (filtered.length === 0) return null;

  return (
    <ul role={depth === 0 ? 'tree' : 'group'}>
      {filtered.map((node) => (
        <TreeNodeRow
          key={node.id}
          node={node}
          tree={tree}
          depth={depth}
          activeNodeId={activeNodeId}
          onActivate={onActivate}
          expanded={expanded}
          toggleExpanded={toggleExpanded}
          matchedIds={matchedIds}
        />
      ))}
    </ul>
  );
}

interface TreeNodeRowProps {
  node: TreeNode;
  tree: Map<string | null, TreeNode[]>;
  depth: number;
  activeNodeId: string | null;
  onActivate: (id: string) => void;
  expanded: Set<string>;
  toggleExpanded: (id: string) => void;
  matchedIds: Set<string> | null;
}

function TreeNodeRow({
  node,
  tree,
  depth,
  activeNodeId,
  onActivate,
  expanded,
  toggleExpanded,
  matchedIds,
}: TreeNodeRowProps) {
  const hasChildren = (tree.get(node.id) ?? []).length > 0;
  const isExpanded = expanded.has(node.id);
  const isActive = activeNodeId === node.id;
  const Icon = iconFor(node.kind);

  const utils = api.useUtils();
  const renameMutation = api.treeNodes.rename.useMutation({
    onSuccess: () => utils.treeNodes.list.invalidate(),
  });
  const deleteMutation = api.treeNodes.delete.useMutation({
    onSuccess: () => utils.treeNodes.list.invalidate(),
  });

  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(node.title ?? '');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const draggable = useDraggable({ id: node.id });
  // Три НЕПЕРЕСЕКАЮЩИЕСЯ drop-зоны (важно для rectIntersection — иначе into
  // всегда побеждает по площади):
  //   before — тонкая полоса 4px над строкой (вставить ВЫШЕ узла)
  //   into   — основная строка (вложить ВНУТРЬ узла)
  //   after  — тонкая полоса 4px под строкой (вставить НИЖЕ узла)
  // Без before/after был только into — пользователь не мог менять порядок siblings.
  const droppableInto = useDroppable({
    id: `drop-into-${node.id}`,
    data: { type: 'into', nodeId: node.id },
  });
  const droppableBefore = useDroppable({
    id: `drop-before-${node.id}`,
    data: { type: 'before', nodeId: node.id },
  });
  const droppableAfter = useDroppable({
    id: `drop-after-${node.id}`,
    data: { type: 'after', nodeId: node.id },
  });

  const setRefs = (el: HTMLElement | null) => {
    draggable.setNodeRef(el);
    droppableInto.setNodeRef(el);
  };

  return (
    <li role="treeitem" aria-expanded={hasChildren ? isExpanded : undefined}>
      {/* Drop-зона "before" — тонкая полоса над строкой. */}
      <div
        ref={droppableBefore.setNodeRef}
        className={clsx(
          'h-1 w-full transition-colors',
          droppableBefore.isOver && 'bg-accent',
        )}
      />
      <div
        ref={setRefs}
        {...draggable.attributes}
        {...draggable.listeners}
        className={clsx(
          'group flex items-center gap-1.5 py-1 pr-1 text-sm rounded-md transition-colors cursor-grab active:cursor-grabbing',
          isActive
            ? 'bg-accent/10 text-accent'
            : 'hover:bg-surface3-light dark:hover:bg-surface3-dark text-ink-light dark:text-ink-dark',
          droppableInto.isOver && 'ring-2 ring-accent/50',
          draggable.isDragging && 'opacity-30',
        )}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
        onClick={() => onActivate(node.id)}
        onDoubleClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          setContextMenu({ x: e.clientX, y: e.clientY });
        }}
      >
        {hasChildren ? (
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(node.id);
            }}
            className="opacity-60 hover:opacity-100"
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        ) : (
          <span className="w-[14px]" />
        )}
        <Icon size={14} style={{ color: node.color ?? undefined }} />
        {editing ? (
          <input
            autoFocus
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (draftTitle.trim() && draftTitle !== node.title) {
                  renameMutation.mutate({ id: node.id, title: draftTitle.trim() });
                }
                setEditing(false);
              }
              if (e.key === 'Escape') {
                setDraftTitle(node.title ?? '');
                setEditing(false);
              }
            }}
            onBlur={() => {
              if (draftTitle.trim() && draftTitle !== node.title) {
                renameMutation.mutate({ id: node.id, title: draftTitle.trim() });
              }
              setEditing(false);
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex-1 px-1 py-0 text-sm bg-surface3-light dark:bg-surface3-dark border border-accent rounded outline-none"
          />
        ) : (
          <span className="truncate flex-1 text-left">{node.title ?? '—'}</span>
        )}
        <span className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(true);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-1 rounded hover:bg-surface3-light dark:hover:bg-surface3-dark text-ink-faint_l dark:text-ink-faint_d hover:text-ink-light dark:hover:text-ink-dark"
            title="Переименовать (или двойной клик)"
          >
            <Pencil size={12} />
          </button>
          <CreateMenu parentId={node.id} parentKind={node.kind} compact />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Удалить «${node.title}»?`)) {
                deleteMutation.mutate({ id: node.id });
              }
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-1 rounded hover:bg-red-500/10 text-ink-faint_l dark:text-ink-faint_d hover:text-red-500"
            title="Удалить"
          >
            <Trash2 size={12} />
          </button>
        </span>
      </div>
      {/* Drop-зона "after" — тонкая полоса под строкой. Помещаем СРАЗУ после
          основной строки и ДО детей: drop в зону after родителя означает
          вставить после ВСЕЙ группы родитель+его развёрнутые дети визуально,
          но логически — сразу после родителя в списке siblings. */}
      <div
        ref={droppableAfter.setNodeRef}
        className={clsx(
          'h-1 w-full transition-colors',
          droppableAfter.isOver && 'bg-accent',
        )}
      />
      {hasChildren && isExpanded && (
        <TreeBranch
          parentId={node.id}
          tree={tree}
          depth={depth + 1}
          activeNodeId={activeNodeId}
          onActivate={onActivate}
          expanded={expanded}
          toggleExpanded={toggleExpanded}
          matchedIds={matchedIds}
        />
      )}

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          items={[
            {
              icon: Pencil,
              label: 'Переименовать',
              onClick: () => setEditing(true),
            },
            {
              icon: Copy,
              label: 'Копировать ID',
              onClick: () => navigator.clipboard.writeText(node.id),
            },
            {
              icon: Trash2,
              label: 'Удалить',
              destructive: true,
              onClick: () => {
                if (window.confirm(`Удалить «${node.title}»?`)) {
                  deleteMutation.mutate({ id: node.id });
                }
              },
            },
          ]}
        />
      )}
    </li>
  );
}

interface ContextMenuItem {
  icon: typeof Pencil;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

function ContextMenu({
  x,
  y,
  onClose,
  items,
}: {
  x: number;
  y: number;
  onClose: () => void;
  items: ContextMenuItem[];
}) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        style={{ left: x, top: y }}
        className="fixed z-50 min-w-[180px] py-1 rounded-md shadow-lg border border-line-light dark:border-line-dark bg-surface-light dark:bg-surface-dark"
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((item, idx) => {
          const Icon = item.icon;
          return (
            <button
              key={idx}
              type="button"
              onClick={() => {
                item.onClick();
                onClose();
              }}
              className={clsx(
                'w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-surface3-light dark:hover:bg-surface3-dark text-left',
                item.destructive && 'text-red-500',
              )}
            >
              <Icon size={14} />
              {item.label}
            </button>
          );
        })}
      </div>
    </>
  );
}

// Проверка: nodeId находится в поддереве, чьим корнем является ancestorId.
// Используется при reorder/into чтобы пользователь не мог переместить узел
// внутрь своего же ребёнка (создание цикла).
function isDescendantOf(
  nodeId: string | null | undefined,
  ancestorId: string,
  byId: Map<string, TreeNode>,
): boolean {
  let cur = nodeId ?? null;
  const visited = new Set<string>();
  while (cur) {
    if (cur === ancestorId) return true;
    if (visited.has(cur)) return false;
    visited.add(cur);
    cur = byId.get(cur)?.parentId ?? null;
  }
  return false;
}

function iconFor(kind: TreeNode['kind']) {
  switch (kind) {
    case 'folder':
      return Folder;
    case 'project':
      return FolderTree;
    case 'space':
      return Sparkles;
    case 'document':
      return FileText;
  }
}

interface CreateMenuProps {
  parentId: string | null;
  parentKind?: TreeNode['kind'];
  compact?: boolean;
}

function CreateMenu({ parentId, parentKind, compact }: CreateMenuProps) {
  const [open, setOpen] = useState(false);
  const utils = api.useUtils();
  const createMutation = api.treeNodes.create.useMutation({
    onSuccess: () => utils.treeNodes.list.invalidate(),
  });

  // Внутри проекта НИЧЕГО создавать нельзя — функционал проекта не определён,
  // он не контейнер для пространств/документов (Даниил 2026-05-01).
  const allowedKinds: TreeNode['kind'][] =
    parentKind === 'project' ? [] : ['folder', 'project', 'space', 'document'];

  const handleCreate = (kind: TreeNode['kind']) => {
    const title = window.prompt(`Название для ${labelOf(kind)}:`, defaultTitle(kind));
    if (!title) return;
    createMutation.mutate({ parentId, kind, title });
    setOpen(false);
  };

  // Если внутри parentKind ничего создавать нельзя — не показываем кнопку
  if (allowedKinds.length === 0) return null;

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        className={clsx(
          'p-1 rounded hover:bg-surface3-light dark:hover:bg-surface3-dark',
          compact && 'opacity-70 hover:opacity-100',
        )}
        aria-label="Создать"
      >
        <Plus size={compact ? 12 : 16} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 min-w-[160px] rounded-md shadow-lg bg-surface-light dark:bg-surface-dark border border-line-light dark:border-line-dark py-1">
            {allowedKinds.map((k) => {
              const I = iconFor(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreate(k);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-surface3-light dark:hover:bg-surface3-dark text-left"
                >
                  <I size={14} />
                  {labelOf(k)}
                </button>
              );
            })}
          </div>
        </>
      )}
    </span>
  );
}

function labelOf(kind: TreeNode['kind']) {
  switch (kind) {
    case 'folder':
      return 'Папка';
    case 'project':
      return 'Проект';
    case 'space':
      return 'Пространство';
    case 'document':
      return 'Документ';
  }
}

function defaultTitle(kind: TreeNode['kind']) {
  switch (kind) {
    case 'folder':
      return 'Новая папка';
    case 'project':
      return 'Новый проект';
    case 'space':
      return 'Новое пространство';
    case 'document':
      return 'Новый документ';
  }
}
